import { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import ShopByLength from "../components/ShopByLength";
import ShopByColor from "../components/ShopByColor";
import WhyKays from "../components/WhyKays";
import Testimonials from "../components/Testimonials";
import Filters from "../components/Filters";
import ProductGrid from "../components/ProductGrid";
import WeaveSection from "../components/WeaveSection";
import HairCareSection from "../components/HairCareSection";
import PixieSection from "../components/PixieSection";
import Footer from "../components/Footer";
import Newsletter from "../components/Newsletter";
import QuickViewDialog from "../components/QuickViewDialog";
import EditProductDialog from "../components/EditProductDialog";
import { fetchFilters, fetchProducts, adminCreateProduct } from "../lib/api";
import { useAdmin } from "../lib/admin";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const DEFAULT_SELECTED = { lengths: [], colors: [], laceTypes: [] };

const useDebounce = (value, delay = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const Shop = () => {
  const [filtersMeta, setFiltersMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(DEFAULT_SELECTED);
  const [priceBounds, setPriceBounds] = useState([0, 300]);
  const [priceRange, setPriceRange] = useState([0, 300]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickView, setQuickView] = useState(null);
  const [editing, setEditing] = useState(null);
  const [category] = useState("Wig");
  const { isAdmin, logout, password } = useAdmin();

  const debouncedSearch = useDebounce(search, 350);
  const debouncedPrice = useDebounce(priceRange, 250);

  // Load filter meta once
  useEffect(() => {
    (async () => {
      try {
        const meta = await fetchFilters();
        setFiltersMeta(meta);
        const min = Math.floor(meta.price_min || 0);
        const max = Math.ceil(meta.price_max || 300);
        setPriceBounds([min, max]);
        setPriceRange([min, max]);
      } catch (e) {
        toast.error("Couldn't load filter options");
      }
    })();
  }, []);

  // Load products on filter change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const priceIsFull =
          debouncedPrice[0] === priceBounds[0] && debouncedPrice[1] === priceBounds[1];
        const data = await fetchProducts({
          lengths: selected.lengths,
          colors: selected.colors,
          laceTypes: selected.laceTypes,
          category,
          minPrice: priceIsFull ? undefined : debouncedPrice[0],
          maxPrice: priceIsFull ? undefined : debouncedPrice[1],
          search: debouncedSearch || undefined,
          sort,
        });
        if (!cancelled) setProducts(data);
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load products");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, debouncedSearch, debouncedPrice, priceBounds, sort, category]);

  const toggle = (key, value) => {
    setSelected((prev) => {
      const exists = prev[key].includes(value);
      return {
        ...prev,
        [key]: exists ? prev[key].filter((v) => v !== value) : [...prev[key], value],
      };
    });
  };

  const clearAll = () => {
    setSelected(DEFAULT_SELECTED);
    setPriceRange(priceBounds);
    setSearch("");
  };

  const activeCount = useMemo(
    () =>
      selected.lengths.length +
      selected.colors.length +
      selected.laceTypes.length +
      (priceRange[0] !== priceBounds[0] || priceRange[1] !== priceBounds[1] ? 1 : 0),
    [selected, priceRange, priceBounds]
  );

  const applyAndScroll = (key, value) => {
    setSelected((prev) => (prev[key].includes(value) ? prev : { ...prev, [key]: [...prev[key], value] }));
    setTimeout(() => {
      const el = document.getElementById("catalog");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        searchValue={search}
        onSearchChange={setSearch}
        onOpenFilters={() => setMobileOpen(true)}
      />

      <Hero />

      <HairCareSection
        onQuickView={(p) => setQuickView(p)}
        onEdit={(p) => setEditing(p)}
      />

      <PixieSection
        onQuickView={(p) => setQuickView(p)}
        onEdit={(p) => setEditing(p)}
      />

      {/* Catalog */}
      <section id="catalog" className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-10 pb-14 lg:pt-14 lg:pb-20">
        <div id="filters" className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--accent))] font-medium mb-2">
              The Collection
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight text-foreground">
              All wigs
            </h2>
            <div className="text-sm text-muted-foreground mt-2">
              <span data-testid="product-count" className="font-semibold text-foreground">
                {loading ? "…" : products.length}
              </span>{" "}
              {products.length === 1 ? "piece" : "pieces"} curated for you
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs uppercase tracking-widest text-muted-foreground">
              Sort
            </span>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger
                data-testid="sort-select"
                className="h-10 w-[200px] rounded-sm border-border/70 bg-white/70"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-testid="sort-options">
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="price_asc">Price: low to high</SelectItem>
                <SelectItem value="price_desc">Price: high to low</SelectItem>
                <SelectItem value="title_asc">Alphabetical (A–Z)</SelectItem>
                <SelectItem value="length_asc">Length: short to long</SelectItem>
                <SelectItem value="length_desc">Length: long to short</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-[280px,1fr] gap-10 lg:gap-16">
          {/* Desktop filters */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              <Filters
                filters={filtersMeta}
                selected={selected}
                onToggle={toggle}
                priceRange={priceRange}
                priceBounds={priceBounds}
                onPriceChange={setPriceRange}
                onClear={clearAll}
                activeCount={activeCount}
              />
            </div>
          </div>

          {/* Grid */}
          <div>
            <ProductGrid
              products={products}
              loading={loading}
              onClearAll={clearAll}
              onOpenFilters={() => setMobileOpen(true)}
              activeFilters={activeCount}
              onQuickView={(p) => setQuickView(p)}
              onEdit={(p) => setEditing(p)}
            />
          </div>
        </div>
      </section>

      <WeaveSection
        onQuickView={(p) => setQuickView(p)}
        onEdit={(p) => setEditing(p)}
      />

      <Marquee />

      <ShopByLength
        available={filtersMeta?.lengths || []}
        onSelect={(v) => applyAndScroll("lengths", v)}
      />

      <WhyKays />

      <ShopByColor
        colors={filtersMeta?.colors || []}
        onSelect={(v) => applyAndScroll("colors", v)}
      />

      <Testimonials />

      <Newsletter />

      <Footer />

      {/* Mobile filters drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-full sm:max-w-sm overflow-y-auto bg-background">
          <SheetHeader className="mb-4">
            <SheetTitle className="font-serif text-3xl text-left">Filter</SheetTitle>
            <SheetDescription className="text-left">
              Refine by length, lace type, color, and price.
            </SheetDescription>
          </SheetHeader>
          <Filters
            filters={filtersMeta}
            selected={selected}
            onToggle={toggle}
            priceRange={priceRange}
            priceBounds={priceBounds}
            onPriceChange={setPriceRange}
            onClear={clearAll}
            activeCount={activeCount}
          />
        </SheetContent>
      </Sheet>

      {/* Quick view dialog */}
      <QuickViewDialog
        product={quickView}
        open={!!quickView}
        onOpenChange={(o) => !o && setQuickView(null)}
      />

      {/* Edit product dialog (admin only) */}
      <EditProductDialog
        product={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={(updated) => {
          setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
          setEditing(null);
        }}
        onDeleted={(deleted) => {
          setProducts((prev) => prev.filter((p) => p.id !== deleted.id));
          setEditing(null);
        }}
      />

      {/* Admin floating badge */}
      {isAdmin && (
        <div
          data-testid="admin-badge"
          className="fixed bottom-5 left-5 z-50 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-4 py-2 rounded-sm shadow-[0_12px_40px_-20px_rgba(0,0,0,0.5)] text-xs uppercase tracking-widest flex items-center gap-3"
        >
          <span>Editing as Kay</span>
          <button
            data-testid="admin-add-product-btn"
            onClick={async () => {
              try {
                const created = await adminCreateProduct(password);
                setProducts((prev) => [created, ...prev]);
                setEditing(created);
                toast.success("New product created — fill in the details and save");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't create product");
              }
            }}
            className="bg-[#B8935F] hover:bg-[#a07f4d] text-white px-2 py-1 rounded-sm normal-case tracking-normal text-[11px]"
          >
            + Add product
          </button>
          <button
            onClick={logout}
            className="text-[#B8935F] hover:text-[hsl(var(--primary-foreground))] underline-offset-2 hover:underline"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default Shop;
